

// /frontend/dashboard/src/router/modules/alertRoutes.js
// Alert route module for alert-centric dashboard pages.
// English explanations are followed by Thai explanations for route-design validation and debugging.

import { debugLog } from '../../utils/debugGate.js';


const ROUTE_DEBUG_NAMESPACE = '[router/alertRoutes]';

function emitAlertRouteDebug(label, payload) {
  debugLog(label, payload);
}

// Static import registry — Vite can statically analyze these at build time.
// English: All component imports must be literal string paths inside this registry.
//          Never use runtime variable strings inside import() — Vite cannot bundle them.
// ไทย: import ทุกตัวต้องเป็น literal string path ใน registry นี้
//      ห้ามใช้ variable string ใน import() เพราะ Vite จะไม่สร้าง chunk สำหรับ production
const PAGE_REGISTRY = {
  DashboardPage: () => import('../../pages/DashboardPage.vue'),
};

function buildLazyPage(registryKey, debugLabel) {
  return async () => {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // emitAlertRouteDebug(
    //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_START] English: starting lazy import for alert route component | ไทย: เริ่ม lazy import สำหรับ component ของ alert route`,
    //   {
    //     registryKey,
    //     debugLabel,
    //   },
    // );

    try {
      const loader = PAGE_REGISTRY[registryKey];

      if (!loader) {
        throw new Error(
          `${ROUTE_DEBUG_NAMESPACE} No registry entry for key: ${registryKey}. Add a static import() entry to PAGE_REGISTRY.`,
        );
      }

      const module = await loader();

      // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
      // emitAlertRouteDebug(
      //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_SUCCESS] English: alert route component loaded successfully | ไทย: โหลด component ของ alert route สำเร็จ`,
      //   {
      //     registryKey,
      //     debugLabel,
      //     exportKeys: Object.keys(module || {}),
      //     hasDefaultExport: Boolean(module && module.default),
      //   },
      // );

      return module;
    } catch (error) {
      console.error(
        `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_ERROR] English: failed to load alert route component | ไทย: โหลด component ของ alert route ไม่สำเร็จ`,
        {
          registryKey,
          debugLabel,
          error: error instanceof Error ? error.message : String(error),
        },
      );
      throw error;
    }
  };
}

function createAlertMeta(meta = {}) {
  const merged = {
    section: 'alert',
    requiresAuth: true,
    requiresAdmin: false,
    layout: 'dashboard',
    navGroup: 'alert',
    debug_en: 'Alert route metadata helps validate whether alert-centric pages are grouped correctly and remain separate from dashboard home, robot pages, mission pages, and admin-only routes.',
    debug_th: 'metadata ของ alert route ใช้ตรวจว่าหน้าที่เกี่ยวกับ alert ถูกจัดกลุ่มอย่างถูกต้องและแยกจาก dashboard หลัก, หน้า robot, หน้า mission และหน้าที่เป็น admin-only หรือไม่',
    ...meta,
  };

  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // emitAlertRouteDebug(
  //   `${ROUTE_DEBUG_NAMESPACE} [META_CREATED] English: created alert route metadata | ไทย: สร้าง metadata ของ alert route แล้ว`,
  //   merged,
  // );

  return merged;
}

const alertRoutes = [
  {
    path: '/alerts',
    redirect: '/alerts/overview',
    meta: createAlertMeta({
      title: 'Alert Root',
      navLabel: 'Alerts',
      routePurpose_en: 'Alert root route redirects to the alert overview page.',
      routePurpose_th: 'route หลักของ alert จะ redirect ไปยังหน้า alert overview',
    }),
  },
  {
    path: '/alerts/overview',
    name: 'AlertOverview',
    component: buildLazyPage('DashboardPage', 'AlertOverviewViaDashboardPage'),
    meta: createAlertMeta({
      title: 'Alert Overview',
      navLabel: 'Overview',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Alert overview route reuses the dashboard page during validation to verify alert panels, alert normalization, and fleet alert summaries.',
      routePurpose_th: 'route ของ alert overview ใช้หน้า dashboard ซ้ำในช่วง validation เพื่อทดสอบ alert panels, การ normalize alert และสรุป alert ระดับ fleet',
      aliasOf: 'DashboardHome',
    }),
  },
  {
    path: '/alerts/live',
    name: 'AlertLive',
    component: buildLazyPage('DashboardPage', 'AlertLiveViaDashboardPage'),
    meta: createAlertMeta({
      title: 'Alert Live',
      navLabel: 'Live',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Alert live route focuses on validating realtime alert streams, alert severity visibility, and active alert rendering.',
      routePurpose_th: 'route ของ alert live ใช้ตรวจ stream ของ alert แบบ realtime, การแสดงระดับความรุนแรง และการ render active alert',
      aliasOf: 'DashboardHome',
    }),
  },
  {
    path: '/alerts/history',
    name: 'AlertHistory',
    component: buildLazyPage('DashboardPage', 'AlertHistoryViaDashboardPage'),
    meta: createAlertMeta({
      title: 'Alert History',
      navLabel: 'History',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Alert history route is reserved for validating future alert history and audit-style pages while temporarily reusing the main dashboard page.',
      routePurpose_th: 'route ของ alert history ถูกเตรียมไว้เพื่อทดสอบหน้าประวัติ alert และหน้าเชิง audit ในอนาคต โดยชั่วคราวยังใช้หน้า dashboard หลักร่วมกัน',
      aliasOf: 'DashboardHome',
    }),
  },
];

// [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
// emitAlertRouteDebug(
//   `${ROUTE_DEBUG_NAMESPACE} [ROUTES_REGISTERED] English: alert route module initialized | ไทย: module ของ alert routes ถูกเตรียมใช้งานแล้ว`,
//   {
//     routeCount: alertRoutes.length,
//     routeNames: alertRoutes.map((route) => route.name || `redirect:${route.path}`),
//     paths: alertRoutes.map((route) => route.path),
//     registryKeys: Object.keys(PAGE_REGISTRY),
//     explanation_en: 'Use this snapshot to verify that alert routes are grouped separately from dashboard, admin, robot, and mission route modules.',
//     explanation_th: 'ใช้ snapshot นี้ตรวจว่า alert routes ถูกแยกออกจาก dashboard, admin, robot และ mission route modules อย่างถูกต้อง',
//   },
// );

export default alertRoutes;
